import { DateTime } from 'luxon';
import { create, type StoreApi, type UseBoundStore } from 'zustand';

import type { WithdrawalLimitItem } from 'utils/payments';
import { trackSprigEvent } from 'utils/sprig';
import { SPRIG_COMPLETED_WITHDRAWAL_FLOW_EVENT_NAME } from 'utils/sprig-constants';

import type { FundMethods } from './registrationStore';

type WithdrawalStep = 'selection' | 'prereview' | 'card' | 'mfa' | 'initiated' | 'failed' | 'billing-address';

interface WithdrawalDialogState {
    step: WithdrawalStep;
    fundMethod: FundMethods | undefined;
    lastInitiatedTime: DateTime;
    loading: boolean;
    errorTitle: string;
    errorMessage: string;
    amount: number;
    cardId: string | undefined;
    bankId: string;
    paymentId: string | undefined;
    cryptoWalletId: string | undefined;
    isStripeWithdrawal: boolean;
    stripeAccountLinked: boolean;
    stripeCardPayoutMethodId: string;
    // True while the "new card via Apple Pay sheet" flow is selected; a linked
    // apple_pay payout method instead rides the pinned-method path with
    // stripeCardPayoutMethodId set and this false.
    isApplePayWithdrawal: boolean;
    braintreeType: 'paypal' | 'venmo' | undefined;
    lockedLimitsDialogOpen: boolean;
    lockedLimitsDialogTitle: string;
    lockedLimitsDialogLockedLimits: WithdrawalLimitItem[];
    meshNetworkSelectionDialogOpen: boolean;
    meshAmountDialogOpen: boolean;
    meshAmountCents: number;
    isMeshMfaStep: boolean;
    meshMethodSelectionDialogOpen: boolean;
    meshManualAddressEntryDialogOpen: boolean;
    // Set when the initiated step was reached via polling timeout rather than a
    // confirmed 'applied' status — the success screen must not claim completion.
    initiatedTimedOut: boolean;
    initiatedExpectedArrivalTs: string | undefined;
    actions: {
        setAmount: (amount: number) => void;
        setCardId: (cardId: string) => void;
        setBankId: (bankId: string) => void;
        setPaymentId: (paymentId: string) => void;
        setCryptoWalletId: (cryptoWalletId: string) => void;
        setIsStripeWithdrawal: (isStripeWithdrawal: boolean) => void;
        setStripeAccountLinked: (linked: boolean) => void;
        setStripeCardPayoutMethodId: (payoutMethodId: string) => void;
        setIsApplePayWithdrawal: (isApplePayWithdrawal: boolean) => void;
        setBraintreeType: (type: 'paypal' | 'venmo' | undefined) => void;
        setLockedLimitsDialogOpen: (open: boolean) => void;
        setLockedLimitsDialogTitle: (title: string) => void;
        setLockedLimitsDialogLockedLimits: (lockedLimits: WithdrawalLimitItem[]) => void;
        setMeshNetworkSelectionDialogOpen: (open: boolean) => void;
        setMeshAmountDialogOpen: (open: boolean) => void;
        setMeshAmountCents: (amountCents: number) => void;
        setIsMeshMfaStep: (isMfaStep: boolean) => void;
        setMeshMethodSelectionDialogOpen: (open: boolean) => void;
        setMeshManualAddressEntryDialogOpen: (open: boolean) => void;
        isInitiated: () => boolean;
        isSelection: () => boolean;
        isPrereviewing: () => boolean;
        setBillingAddress: () => void;
        setSelection: () => void;
        setPrereviewing: () => void;
        setCard: () => void;
        setMfa: () => void;
        setInitiated: (opts?: { timedOut?: boolean; expectedArrivalTs?: string }) => void;
        setFailed: (errorTitle?: string, errorMessage?: string) => void;
        setFundMethod: (method: FundMethods | undefined) => void;
        reset: () => void;
    };
}

export const useWithdrawalDialogStore: UseBoundStore<StoreApi<WithdrawalDialogState>> = create<WithdrawalDialogState>()(
    (set) => ({
        step: 'selection',
        fundMethod: undefined,
        lastInitiatedTime: DateTime.now(),
        loading: false,
        errorTitle: '',
        errorMessage: '',
        amount: 0,
        cardId: undefined,
        bankId: '',
        paymentId: undefined,
        cryptoWalletId: undefined,
        isStripeWithdrawal: false,
        stripeAccountLinked: false,
        stripeCardPayoutMethodId: '',
        isApplePayWithdrawal: false,
        braintreeType: undefined,
        initiatedTimedOut: false,
        initiatedExpectedArrivalTs: undefined,
        lockedLimitsDialogOpen: false,
        lockedLimitsDialogTitle: '',
        lockedLimitsDialogLockedLimits: [],
        meshNetworkSelectionDialogOpen: false,
        meshAmountDialogOpen: false,
        meshAmountCents: 0,
        isMeshMfaStep: false,
        meshMethodSelectionDialogOpen: false,
        meshManualAddressEntryDialogOpen: false,
        actions: {
            setAmount: (amount) => set({ amount }),
            setCardId: (cardId) => set({ cardId }),
            setBankId: (bankId) => set({ bankId }),
            setPaymentId: (paymentId) => set({ paymentId }),
            setCryptoWalletId: (cryptoWalletId) => set({ cryptoWalletId }),
            setIsStripeWithdrawal: (isStripeWithdrawal) => set({ isStripeWithdrawal }),
            setStripeAccountLinked: (stripeAccountLinked) => set({ stripeAccountLinked }),
            setStripeCardPayoutMethodId: (stripeCardPayoutMethodId) => set({ stripeCardPayoutMethodId }),
            setIsApplePayWithdrawal: (isApplePayWithdrawal) => set({ isApplePayWithdrawal }),
            setBraintreeType: (braintreeType) => set({ braintreeType }),
            setLockedLimitsDialogOpen: (lockedLimitsDialogOpen) => set({ lockedLimitsDialogOpen }),
            setLockedLimitsDialogTitle: (lockedLimitsDialogTitle) => set({ lockedLimitsDialogTitle }),
            setLockedLimitsDialogLockedLimits: (lockedLimitsDialogLockedLimits) =>
                set({ lockedLimitsDialogLockedLimits }),
            setMeshNetworkSelectionDialogOpen: (meshNetworkSelectionDialogOpen) =>
                set({ meshNetworkSelectionDialogOpen }),
            setMeshAmountDialogOpen: (meshAmountDialogOpen) => set({ meshAmountDialogOpen }),
            setMeshAmountCents: (meshAmountCents) => set({ meshAmountCents }),
            setIsMeshMfaStep: (isMeshMfaStep) => set({ isMeshMfaStep }),
            setMeshMethodSelectionDialogOpen: (meshMethodSelectionDialogOpen) => set({ meshMethodSelectionDialogOpen }),
            setMeshManualAddressEntryDialogOpen: (meshManualAddressEntryDialogOpen) =>
                set({ meshManualAddressEntryDialogOpen }),
            isInitiated: () => useWithdrawalDialogStore.getState().step === 'initiated',
            isSelection: () => useWithdrawalDialogStore.getState().step === 'selection',
            isPrereviewing: () => useWithdrawalDialogStore.getState().step === 'prereview',
            setSelection: () => set({ step: 'selection' }),
            setPrereviewing: () => set({ step: 'prereview' }),
            setCard: () => set({ step: 'card' }),
            setMfa: () => set({ step: 'mfa' }),
            setInitiated: (opts?: { timedOut?: boolean; expectedArrivalTs?: string }) => {
                set({
                    step: 'initiated',
                    lastInitiatedTime: DateTime.now(),
                    initiatedTimedOut: opts?.timedOut ?? false,
                    initiatedExpectedArrivalTs: opts?.expectedArrivalTs,
                });
                trackSprigEvent({ eventName: SPRIG_COMPLETED_WITHDRAWAL_FLOW_EVENT_NAME });
            },
            setFailed: (errorTitle?: string, errorMessage?: string) =>
                set({
                    // reset fields
                    amount: 0,
                    cardId: undefined,
                    bankId: '',
                    paymentId: undefined,
                    cryptoWalletId: undefined,
                    braintreeType: undefined,
                    isApplePayWithdrawal: false,
                    fundMethod: undefined,
                    loading: false,
                    // failed state
                    step: 'failed',
                    errorTitle: errorTitle || 'Your withdrawal failed',
                    errorMessage: errorMessage || 'Review entered info and try again',
                }),
            setBillingAddress: () => set({ step: 'billing-address' }),
            setFundMethod: (fundMethod) => set({ fundMethod }),
            reset: () =>
                set({
                    amount: 0,
                    cardId: undefined,
                    bankId: '',
                    paymentId: undefined,
                    cryptoWalletId: undefined,
                    braintreeType: undefined,
                    isApplePayWithdrawal: false,
                    fundMethod: undefined,
                    loading: false,
                    errorTitle: '',
                    errorMessage: '',
                }),
        },
    }),
);
